import React, { createContext, useState, useContext, useMemo, ReactNode } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { paletteDark, paletteLight } from '../../theme/palette';
import { typography } from '../../theme/typography';
import { buildComponents } from '../../theme/components';

interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/** The user's last choice, so a reload does not silently drop them back to light. */
const STORAGE_KEY = 'gtThemeMode';

const readStoredMode = (): 'light' | 'dark' => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

/**
 * Themes are built in two passes. The first `createTheme` produces the palette,
 * typography and spacing; `buildComponents` then reads that finished theme to
 * derive every component override from real palette values, so the whole
 * component layer follows Setting → Theme Color rather than hardcoding colour.
 */
const buildTheme = (mode: 'light' | 'dark') => {
  const base = createTheme({
    palette: mode === 'light' ? paletteLight : paletteDark,
    typography,
  });
  return createTheme(base, { components: buildComponents(base) });
};

const themeLight = buildTheme('light');
const themeDark = buildTheme('dark');

export const ThemeProviderWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(readStoredMode);

  const theme = mode === 'light' ? themeLight : themeDark;

  const toggleTheme = () => {
    setMode((prevMode) => {
      const next = prevMode === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* Private-mode or blocked storage: the toggle still works for this session. */
      }
      return next;
    });
  };

  const value = useMemo(() => ({ mode, toggleTheme }), [mode]);

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProviderWrapper');
  }
  return context;
};
